import { Link } from "react-router-dom";
import type { SiteWithCategory } from "@/types/nav";
import { formatVisits } from "@/lib/navData";
import { getTone } from "@/lib/tone";
import FavoriteButton from "@/components/feature/FavoriteButton";

interface SiteCardProps {
  site: SiteWithCategory;
  showCategory?: boolean;
}

export default function SiteCard({ site, showCategory = true }: SiteCardProps) {
  const tone = getTone(site.tone);

  return (
    <article className="group flex flex-col sm:flex-row sm:items-center gap-4 p-4 md:p-5 rounded-xl border border-background-200 bg-background-50 hover:border-primary-300 transition-colors">
      <Link
        to={`/detail/${site.id}`}
        className={`w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br ${tone.gradient} text-background-50 flex items-center justify-center font-heading font-bold text-lg shrink-0`}
        aria-label={`查看${site.name}详情`}
      >
        {site.logoText}
      </Link>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/detail/${site.id}`}
            className={`font-heading font-semibold text-base text-foreground-950 ${tone.hoverText} transition-colors cursor-pointer`}
          >
            {site.name}
          </Link>
          {showCategory && (
            <Link
              to={`/category/${encodeURIComponent(site.categoryName)}`}
              className={`text-xs px-2 py-0.5 rounded-full ${tone.softBg} ${tone.softText} hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap`}
            >
              {site.categoryName}
            </Link>
          )}
          <span className="text-xs text-foreground-400 flex items-center gap-1 whitespace-nowrap">
            <i className="ri-fire-line text-accent-500" />
            {formatVisits(site.visits)}
          </span>
        </div>
        <p className="text-sm text-foreground-600 mt-1.5 line-clamp-2 leading-relaxed">
          {site.description}
        </p>
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {site.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 rounded bg-background-100 text-foreground-500"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div className="flex sm:flex-col lg:flex-row items-center gap-2 shrink-0">
        <FavoriteButton siteId={site.id} />
        <Link
          to={`/detail/${site.id}`}
          className="flex-1 sm:flex-none text-center px-4 py-2.5 rounded-md border border-background-200 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          详情
        </Link>
        <a
          href={site.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 sm:flex-none text-center px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          快速访问
          <i className="ri-external-link-line ml-1" />
        </a>
      </div>
    </article>
  );
}